import { noop } from 'lodash';

import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';
import { Patient } from '@dreryk/egabinet-patient-frontend-api';

import {
   ButtonVariant,
   LinkVariant,
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalFooterPosition,
   ModalLink,
   ModalWidth,
   RemoveIcon,
} from '@dreryk/react-components';

import { VisitDiagnoses } from '~libs.shared.visit';

import { medicineTranslationKeys } from '../../../translations';

import { MedicineReplacementForm } from './form/MedicineReplacementForm';
import { MedicineReplacementSource } from './medicineReplacement.types';
import { WithdrawnMedicineRemoveActionInterceptor } from './interceptors/WithdrawnMedicineRemoveActionInterceptor';
import { MedicineReplaceExpiredActionInterceptor } from './interceptors/MedicineReplaceExpiredActionInterceptor';

type MedicineReplacementModalProps = {
   readonly onClose: VoidFunction;
   readonly withdrawnMedicine: MedicineSimple;
   readonly patientId: Patient['id'];
   readonly medicineId: number;
   readonly visitDiagnoses: VisitDiagnoses;
   readonly source: MedicineReplacementSource;
};

export const MedicineReplacementModal = (props: MedicineReplacementModalProps): JSX.Element => {
   return (
      <Modal
         title={medicineTranslationKeys.replacement.replaceWithdrawnMedicineToAnotherOne}
         width={ModalWidth.WIDE}
         testId={'medicineReplacement'}
      >
         <MedicineReplacementForm
            withdrawnMedicine={props.withdrawnMedicine}
            patientId={props.patientId}
            visitDiagnoses={props.visitDiagnoses}
         >
            {({ handleSubmit, formState, newMedicineSelected }) => (
               <>
                  <WithdrawnMedicineRemoveActionInterceptor source={props.source} medicineId={props.medicineId}>
                     <ModalLink
                        testId={'removeWithdrawnMedicine'}
                        variant={LinkVariant.ERROR}
                        position={ModalFooterPosition.SUPPLEMENTARY}
                        onClick={noop}
                        label={medicineTranslationKeys.replacement.removeWithdrawnMedicine}
                        icon={<RemoveIcon />}
                     />
                  </WithdrawnMedicineRemoveActionInterceptor>
                  <ModalCancelButton onClick={props.onClose} shouldConfirmExit={formState.isDirty} />
                  <MedicineReplaceExpiredActionInterceptor
                     source={props.source}
                     handleSubmit={handleSubmit}
                     patientId={props.patientId}
                  >
                     <ModalConfirmButton
                        testId={'replaceMedicineForPatient'}
                        label={
                           props.source === 'favorite'
                              ? medicineTranslationKeys.replacement.replaceMedicine
                              : medicineTranslationKeys.replacement.replaceMedicineForPatient
                        }
                        variant={props.source === 'favorite' ? ButtonVariant.PRIMARY : ButtonVariant.SECONDARY}
                        onClick={props.onClose}
                        disabled={!newMedicineSelected}
                     />
                  </MedicineReplaceExpiredActionInterceptor>
                  {props.source === 'continuouslyUsedMedicine' ? (
                     <MedicineReplaceExpiredActionInterceptor source={props.source} handleSubmit={handleSubmit}>
                        <ModalConfirmButton
                           testId={'replaceMedicineForAll'}
                           label={medicineTranslationKeys.replacement.replaceMedicineForAll}
                           onClick={props.onClose}
                           disabled={!newMedicineSelected}
                        />
                     </MedicineReplaceExpiredActionInterceptor>
                  ) : null}
                  <ModalCloseButton onClick={props.onClose} shouldConfirmExit={formState.isDirty} />
               </>
            )}
         </MedicineReplacementForm>
      </Modal>
   );
};
